import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, QrCodeIcon, ScanBarcodeIcon, CheckCircle2Icon, KeyboardIcon } from 'lucide-react';
interface PaymentModalProps {
  type: 'QR' | 'Coupon';
  amount: number;
  onConfirm: () => void;
  onClose: () => void;
}
export function PaymentModal({
  type,
  amount,
  onConfirm,
  onClose
}: PaymentModalProps) {
  const [couponCode, setCouponCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [qrTimer, setQrTimer] = useState(300); // 5 min timer
  // QR timer countdown
  useEffect(() => {
    if (type !== 'QR') return;
    const interval = setInterval(() => {
      setQrTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [type]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Simulate barcode scan
  const handleSimulateScan = () => {
    setScanning(true);
    setTimeout(() => {
      setCouponCode('COUP-' + Math.floor(1000 + Math.random() * 9000));
      setScanning(false);
      setScanComplete(true);
    }, 2000);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-id="element-117">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.2
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden" data-id="element-118">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-119">
          <div className="flex items-center gap-2" data-id="element-120">
            {type === 'QR' ? <QrCodeIcon className="w-5 h-5 text-brand-teal" data-id="element-121" /> : <ScanBarcodeIcon className="w-5 h-5 text-brand-teal" data-id="element-122" />}
            <h3 className="text-lg font-bold text-slate-900" data-id="element-123">
              {type === 'QR' ? 'QR Code Payment' : 'Coupon Payment'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-124">
            <XIcon className="w-5 h-5" data-id="element-125" />
          </button>
        </div>

        <div className="p-6" data-id="element-126">
          {type === 'QR' && <div className="text-center" data-id="element-127">
              {/* Amount */}
              <div className="mb-4" data-id="element-128">
                <p className="text-xs text-slate-400 font-medium" data-id="element-129">
                  Amount to Pay
                </p>
                <p className="text-2xl font-bold text-brand-teal" data-id="element-130">
                  ₹{amount.toFixed(2)}
                </p>
              </div>

              {/* QR Code */}
              <div className="bg-white border-2 border-slate-200 rounded-2xl p-4 inline-block mb-4" data-id="element-131">
                <svg viewBox="0 0 200 200" className="w-48 h-48 mx-auto" data-id="element-132">
                  {/* Simulated QR pattern */}
                  <rect width="200" height="200" fill="white" data-id="element-133" />
                  {/* Corner squares */}
                  <rect x="10" y="10" width="50" height="50" fill="black" data-id="element-134" />
                  <rect x="15" y="15" width="40" height="40" fill="white" data-id="element-135" />
                  <rect x="20" y="20" width="30" height="30" fill="black" data-id="element-136" />

                  <rect x="140" y="10" width="50" height="50" fill="black" data-id="element-137" />
                  <rect x="145" y="15" width="40" height="40" fill="white" data-id="element-138" />
                  <rect x="150" y="20" width="30" height="30" fill="black" data-id="element-139" />

                  <rect x="10" y="140" width="50" height="50" fill="black" data-id="element-140" />
                  <rect x="15" y="145" width="40" height="40" fill="white" data-id="element-141" />
                  <rect x="20" y="150" width="30" height="30" fill="black" data-id="element-142" />

                  {/* Data pattern */}
                  {Array.from({
                length: 12
              }, (_, row) => Array.from({
                length: 12
              }, (_, col) => {
                const show = (row * 7 + col * 13 + row * col) % 3 !== 0;
                if (!show) return null;
                return <rect key={`${row}-${col}`} x={70 + col * 5} y={70 + row * 5} width="4" height="4" fill="black" data-id="element-143" />;
              }))}
                  {Array.from({
                length: 6
              }, (_, i) => <Fragment key={`extra-${i}`} data-id="element-144">
                        <rect x={70 + i * 10} y="15" width="5" height="5" fill="black" data-id="element-145" />
                        <rect x={70 + i * 10} y="25" width="5" height="5" fill={i % 2 === 0 ? 'black' : 'white'} data-id="element-146" />
                        <rect x="15" y={70 + i * 10} width="5" height="5" fill="black" data-id="element-147" />
                        <rect x="25" y={70 + i * 10} width="5" height="5" fill={i % 2 === 0 ? 'black' : 'white'} data-id="element-148" />
                      </Fragment>)}
                </svg>
              </div>

              {/* UPI Info */}
              <p className="text-xs text-slate-500 mb-1" data-id="element-149">
                Scan with any UPI app
              </p>
              <p className="text-xs text-slate-400 font-mono mb-3" data-id="element-150">
                UPI ID: erplogipark@ybl
              </p>

              {/* Timer */}
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-151">
                <div className={`text-xs font-medium px-3 py-1 rounded-full ${qrTimer > 60 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`} data-id="element-152">
                  Expires in {formatTime(qrTimer)}
                </div>
              </div>

              <button onClick={onConfirm} className="w-full inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors" data-id="element-153">
                <CheckCircle2Icon className="w-4 h-4" data-id="element-154" />
                Payment Received — Confirm
              </button>
            </div>}

          {type === 'Coupon' && <div data-id="element-155">
              {/* Scanner Area */}
              <div className="mb-5" data-id="element-156">
                <label className="block text-xs font-semibold text-slate-500 mb-2" data-id="element-157">
                  Scan Barcode
                </label>
                <div onClick={handleSimulateScan} className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-brand-teal/50 hover:bg-brand-teal/5 transition-colors" data-id="element-158">
                  {scanning ? <div className="flex flex-col items-center gap-2" data-id="element-159">
                      <motion.div animate={{
                  opacity: [1, 0.3, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5
                }} data-id="element-160">
                        <ScanBarcodeIcon className="w-10 h-10 text-brand-teal" data-id="element-161" />
                      </motion.div>
                      <p className="text-sm text-brand-teal font-medium" data-id="element-162">
                        Scanning...
                      </p>
                    </div> : scanComplete ? <div className="flex flex-col items-center gap-2" data-id="element-163">
                      <CheckCircle2Icon className="w-10 h-10 text-green-500" data-id="element-164" />
                      <p className="text-sm text-green-600 font-medium" data-id="element-165">
                        Barcode scanned successfully!
                      </p>
                    </div> : <div className="flex flex-col items-center gap-2" data-id="element-166">
                      <ScanBarcodeIcon className="w-10 h-10 text-slate-300" data-id="element-167" />
                      <p className="text-sm text-slate-500" data-id="element-168">
                        Tap to scan barcode
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-169">
                        Point the barcode at the scanner
                      </p>
                    </div>}
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-5" data-id="element-170">
                <div className="flex-1 h-px bg-slate-200" data-id="element-171" />
                <span className="text-xs text-slate-400 font-medium" data-id="element-172">OR</span>
                <div className="flex-1 h-px bg-slate-200" data-id="element-173" />
              </div>

              {/* Manual Entry */}
              <div className="mb-5" data-id="element-174">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-175">
                  Enter Coupon Code Manually
                </label>
                <div className="relative" data-id="element-176">
                  <KeyboardIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-177" />
                  <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. COUP-1234" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-178" />
                </div>
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 mb-5" data-id="element-179">
                <span className="text-sm font-medium text-slate-600" data-id="element-180">
                  Amount
                </span>
                <span className="text-lg font-bold text-brand-teal" data-id="element-181">
                  ₹{amount.toFixed(2)}
                </span>
              </div>

              <button onClick={onConfirm} disabled={!couponCode.trim()} className="w-full inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-182">
                <CheckCircle2Icon className="w-4 h-4" data-id="element-183" />
                Apply Coupon & Confirm
              </button>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}